import { useEffect, useRef } from 'react';
import { useItineraryStore } from '../store/itinerary-store';
import { trpc } from '../lib/trpc';
import { useAuth } from '../contexts/AuthContext';
import type { TripReference } from '@my-fullstack-app/itinerary-types';

/**
 * Background reference suggester. Two passes:
 *
 *   1. Trip-level — for any trip with `references === undefined`, fire
 *      `references.suggestForTrip` (REST Countries → country-info +
 *      currency cards).
 *   2. Place-level — for any place with `references === undefined`,
 *      fire `references.suggestForPlace` (Wikipedia article + map
 *      link). Iterates every trip's places independently.
 *
 * Both passes are gated by per-entity Sets so React Strict Mode
 * double-renders don't double-fire, and so a hydrate that returns
 * `references: []` (empty array — already asked) doesn't trigger
 * another round. Failures swallow into a console.warn — auto-suggest
 * is best-effort.
 *
 * Mount once near the root (alongside SyncManagerInit).
 */
export function useReferenceSuggester(): void {
  const utils = trpc.useUtils();
  const { isAuthenticated } = useAuth();
  const trips = useItineraryStore((s) => s.trips);
  const addReferences = useItineraryStore((s) => s.addReferences);
  const addPlaceReferences = useItineraryStore((s) => s.addPlaceReferences);
  // Per-session guards.
  const askedTripsRef = useRef<Set<string>>(new Set());
  const askedPlacesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) return;

    // ---- Trip-level pass ----
    const tripCandidates = trips.filter(
      (t) => t.references === undefined && !askedTripsRef.current.has(t.id),
    );
    for (const trip of tripCandidates) {
      askedTripsRef.current.add(trip.id);
      void (async () => {
        try {
          const result = await utils.client.references.suggestForTrip.mutate({ tripId: trip.id });
          const refs = result.references as TripReference[];
          if (refs.length > 0) {
            addReferences(trip.id, refs);
          } else {
            // Mark with empty array so subsequent renders don't try
            // again. Goes through addReferences with an empty list —
            // but addReferences early-returns when refs.length === 0,
            // so we have to reach into the store directly.
            useItineraryStore.setState((s) => ({
              trips: s.trips.map((t) =>
                t.id === trip.id && t.references === undefined ? { ...t, references: [] } : t,
              ),
            }));
          }
        } catch (err) {
          console.warn('[references] suggestForTrip failed for', trip.id, err);
        }
      })();
    }

    // ---- Place-level pass ----
    for (const trip of trips) {
      for (const day of trip.days) {
        for (const place of day.places) {
          if (place.references !== undefined) continue;
          if (askedPlacesRef.current.has(place.id)) continue;
          askedPlacesRef.current.add(place.id);
          void (async () => {
            try {
              const result = await utils.client.references.suggestForPlace.mutate({
                tripId: trip.id,
                placeId: place.id,
              });
              const refs = result.references as TripReference[];
              if (refs.length > 0) {
                addPlaceReferences(trip.id, place.id, refs);
              } else {
                useItineraryStore.setState((s) => ({
                  trips: s.trips.map((t) => {
                    if (t.id !== trip.id) return t;
                    return {
                      ...t,
                      days: t.days.map((d) => ({
                        ...d,
                        places: d.places.map((p) =>
                          p.id === place.id && p.references === undefined ? { ...p, references: [] } : p,
                        ),
                      })),
                    };
                  }),
                }));
              }
            } catch (err) {
              console.warn('[references] suggestForPlace failed for', place.id, err);
            }
          })();
        }
      }
    }
  }, [isAuthenticated, trips, addReferences, addPlaceReferences, utils]);
}
